import { ComponentProps, forwardRef } from 'react';

import cx from 'classnames';

import { Button as ShadcnButton } from '../shadcn/Button';
import { TSizesExtended, TColors } from '../../types';
import { TIconImages } from '../icons/codegen/types';
import { Icon, TIconRotate } from '../icons/Icon';

// Tailwind classes for colors
const COLOR_CLASSES: Record<TColors, string> = {
  primary: 'text-primary',
  accent: 'text-accent',
  text: 'text-text',
  secondary: 'text-text-secondary',
  note: 'text-text-note',
  contrast: 'text-text-contrast',
  secondaryContrast: 'text-text-secondary-contrast',
  noteContrast: 'text-text-note-contrast',
  error: 'text-error',
  orange: 'text-orange',
  warning: 'text-warning',
  success: 'text-success',
  info: 'text-info',
  lavender: 'text-lavender',
};

// Tailwind classes for sizes
const SIZE_CLASSES: Record<TSizesExtended, string> = {
  xs: 'text-xs',
  s: 'text-sm',
  m: 'text-base',
  l: 'text-lg',
  xl: 'text-xl',
};

// Map button size to icon size
const ICON_SIZE_MAP: Partial<Record<TSizesExtended, TSizesExtended>> = {
  xs: 'xs',
  s: 'xs',
  m: 's',
  l: 'm',
  xl: 'l',
};

export type TButtonTextProps = {
  color?: TColors;
  iconEnd?: TIconImages;
  iconRotate?: TIconRotate;
  iconSize?: TSizesExtended;
  iconStart?: TIconImages;
  iconType?: 'flat' | 'colored';
  isMultiline?: boolean;
  isSubmitting?: boolean;
  isUnderline?: boolean;
  size?: TSizesExtended;
} & Omit<ComponentProps<'button'>, 'ref'>;

export const ButtonText = forwardRef<HTMLButtonElement, TButtonTextProps>(
  function ButtonText(
    {
      children,
      className,
      color = 'text',
      disabled,
      iconEnd,
      iconRotate,
      iconSize,
      iconStart,
      iconType,
      isMultiline,
      isSubmitting,
      isUnderline,
      size = 'm',
      type = 'button',
      onClick,
      ...rest
    },
    ref,
  ) {
    // Determine button size for shadcn (s or m)
    const shadcnSize = size === 'xs' || size === 's' ? 's' : 'm';

    const buttonClassName = cx(
      COLOR_CLASSES[color],
      SIZE_CLASSES[size],
      isMultiline && 'whitespace-pre-line',
      isUnderline && 'underline',
      onClick && 'cursor-pointer',
      !onClick && 'cursor-default',
    );

    return (
      <ShadcnButton
        ref={ref}
        variant="text"
        size={shadcnSize}
        className={cx(buttonClassName, className)}
        disabled={disabled || isSubmitting}
        type={type}
        onClick={onClick}
        {...rest}
      >
        {iconStart && (
          <Icon
            color={color}
            img={iconStart}
            rotate={iconRotate}
            size={iconSize || ICON_SIZE_MAP[size] || 's'}
            type={iconType}
          />
        )}
        {children}
        {iconEnd && (
          <Icon
            color={color}
            img={iconEnd}
            rotate={iconRotate}
            size={iconSize || ICON_SIZE_MAP[size] || 's'}
            type={iconType}
          />
        )}
      </ShadcnButton>
    );
  },
);
